/**
 * @file     StressTestLink.tsx
 * @purpose  "Stress test this mortgage" for the listing cost section: opens
 *           /mortgage-stress-test prefilled with the cost rail's current
 *           down payment, rate and amortization, and the listing's tax and
 *           strata. Sends the same listing_stress_test_opened event the
 *           calculator card sends.
 */
'use client';

import Link from 'next/link';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { hubColors } from '@/components/hub/hubKitStyles';
import { fontWeightTokens } from '@/design-system/tokens/typography';
import { useAnalytics } from '@/hooks/useAnalytics';
import { estimateAnnualPropertyTax } from '@/utils/finance/mortgage';
import { useListingMortgageState } from './ListingMortgageState';

interface Props {
  listPrice: number;
  taxAnnualAmount: number | null;
  associationFee: number | null;
  listingKey: string;
  listingId: string | null;
}

export function StressTestLink({ listPrice, taxAnnualAmount, associationFee, listingKey, listingId }: Props) {
  const { t } = useTranslation();
  const { trackEvent } = useAnalytics();
  const state = useListingMortgageState();
  if (!state) return null;
  const downPayment = Math.round(listPrice * (state.downPct / 100));
  const tax = taxAnnualAmount ?? estimateAnnualPropertyTax(listPrice);
  const href = `/mortgage-stress-test?homePrice=${listPrice}&downPayment=${downPayment}&rate=${state.rate}&amortization=${state.amortization}&tax=${tax}&hoa=${associationFee ?? ''}&source=listing_${listingKey}`;

  return (
    <Box
      component={Link}
      href={href}
      onClick={() =>
        trackEvent({
          eventName: 'listing_stress_test_opened',
          eventType: 'click',
          category: 'engagement',
          metadata: { listing_key: listingKey, listing_id: listingId },
        })
      }
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        minHeight: 44,
        fontSize: 15,
        fontWeight: fontWeightTokens.medium,
        color: hubColors.link,
        textDecoration: 'none',
        '@media (hover: hover)': { '&:hover': { color: hubColors.linkHover, textDecoration: 'underline' } },
        '&:focus-visible': { outline: `2px solid ${hubColors.interactiveFocusRing}`, outlineOffset: 2, borderRadius: '4px' },
      }}
    >
      {t('listingDetail.costs.stressTest', { defaultValue: 'Stress test this mortgage' })}
    </Box>
  );
}
